// Core entity types
export interface Device {
  id: string;
  serialNumber: string;
  sku: string;
  condition: DeviceCondition;
  components: Component[];
  receivedDate: string;
  receivedBy: string;
  notes?: string;
}

export interface DeviceManagementDevice {
  id: string;
  serialNumber: string;
  sku: string;
  customerName: string;
  condition: string;
  status: string;
  issue: string;
  receivedDate: string;
  receivedBy: string;
  updatedDate?: string;
  updatedBy?: string;
}

export interface Component {
  id: string;
  partId: string;
  serialNumber?: string;
  condition: ComponentCondition;
  grade?: ComponentGrade;
  notes?: string;
  photos?: string[];
  testResults?: string;
  gradeHistory: GradeHistoryEntry[];
}

export interface Part {
  id: string;
  sku: string;
  name: string;
  description: string;
  costPrice: number;
  partType: PartType;
  bomCompatible: boolean;
  serialised: boolean;
  compatibleModels: string[]; // Array of Model IDs
}

export interface Model {
  id: string;
  name: string;
  manufacturer: string;
  category: string;
  description?: string;
  releaseYear?: number;
  discontinued: boolean;
  createdDate: string;
  createdBy: string;
  lastModified: string;
  lastModifiedBy: string;
}

export interface ModelCategory {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdDate: string;
  createdBy: string;
  lastModified: string;
  lastModifiedBy: string;
}

export interface Manufacturer {
  id: string;
  name: string;
  description?: string;
  website?: string;
  isActive: boolean;
  createdDate: string;
  createdBy: string;
  lastModified: string;
  lastModifiedBy: string;
}

export interface BillOfMaterials {
  id: string;
  name: string;
  deviceSku: string;
  version: string;
  parts: BOMPart[];
  createdDate: string;
  createdBy: string;
  isActive: boolean;
}

export interface BOMPart {
  partId: string;
  quantity: number;
  minGrade?: ComponentGrade;
  allowedGrades: ComponentGrade[];
  isRequired: boolean;
}

export interface WorkOrder {
  id: string;
  deviceId: string;
  deviceSerialNumber: string;
  deviceCategory: string;
  workOrderType: WorkOrderType;
  location: string;
  operator: string;
  faults: DeviceFault[];
  partsUsed: WorkOrderPart[];
  activities: WorkOrderActivity[];
  targetBom?: string; // BOM ID for kitting work orders
  startTime: string;
  endTime?: string;
  totalTimeMinutes?: number;
  status: WorkOrderStatus;
  qualityInspectionPassed?: boolean;
  notes?: string;
}

export interface WorkOrderPart {
  partId: string;
  componentId?: string;
  quantity: number;
  timeUsedMinutes: number;
}

export interface WorkOrderActivity {
  id: string;
  description: string;
  isCompleted: boolean;
  completedAt?: string;
  completedBy?: string;
  estimatedMinutes?: number;
  actualMinutes?: number;
  notes?: string;
}

export interface WorkOrderTypeDefaults {
  id: string;
  workOrderType: WorkOrderType;
  defaultActivities: DefaultActivity[];
  isActive: boolean;
  createdDate: string;
  createdBy: string;
  lastModified: string;
  lastModifiedBy: string;
}

export interface DefaultActivity {
  id: string;
  description: string;
  estimatedMinutes: number;
  isRequired: boolean;
  order: number;
}

export interface DeviceFault {
  id: string;
  description: string;
  deviceCategory: string;
  isActive: boolean;
}

export interface InventoryItem {
  partId: string;
  grade: ComponentGrade;
  quantity: number;
  totalValue: number;
  components: string[]; // Component IDs
}

export interface AuditLogEntry {
  id: string;
  userId: string;
  action: string;
  entityType: string;
  entityId: string;
  timestamp: string;
  details?: Record<string, any>;
  userInfo?: {
    username: string;
    name: string;
    email: string;
  };
}

export interface GradeHistoryEntry {
  id: string;
  previousGrade?: ComponentGrade;
  newGrade: ComponentGrade;
  gradedBy: string;
  gradedDate: string;
  notes?: string;
}

// Enum types
export enum DeviceCondition {
  WORKING = 'working',
  REPAIRABLE = 'repairable',
  RECYCLED = 'recycled',
  SCRAP = 'scrap'
}

export enum ComponentCondition {
  HARVESTED = 'harvested',
  INSTALLED = 'installed',
  QUARANTINED = 'quarantined',
  SCRAPPED = 'scrapped'
}

export enum ComponentGrade {
  NEW = 'new',
  UNUSED = 'unused',
  GRADE_A = 'grade_a',
  GRADE_B = 'grade_b',
  GRADE_C = 'grade_c',
  SCRAP = 'scrap'
}

export enum PartType {
  WAND = 'wand',
  HEAD = 'head',
  BATTERY = 'battery',
  HOUSING = 'housing',
  PROCESSOR = 'processor',
  SENSOR = 'sensor',
  CABLE = 'cable',
  OTHER = 'other'
}

export enum WorkOrderStatus {
  CREATED = 'created',
  IN_PROGRESS = 'in_progress',
  AWAITING_PARTS = 'awaiting_parts',
  QUALITY_INSPECTION = 'quality_inspection',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export enum WorkOrderType {
  RECYCLING = 'recycling',
  REFURBISHMENT = 'refurbishment',
  KITTING = 'kitting'
}

// UI types
export interface TabItem {
  id: string;
  label: string;
  component: React.ComponentType;
}

export interface User {
  id: string;
  username: string;
  email: string;
  role: UserRole;
}

export enum UserRole {
  ADMIN = 'admin',
  OPERATOR = 'operator',
  VIEWER = 'viewer'
}